import { gql } from "@apollo/client";

export const CURRENCY = gql`
	fragment currencyFields on Currency {
		id
		name
		symbol
		abbreviation
		decimalSeparator
		thousandsSeparator
	}
`;

export const MONEY = gql`
	fragment moneyFields on Money {
		name
		value
		currency {
			...currencyFields
		}
	}
	${CURRENCY}
`;

export const MONEY_SERIES_POINT = gql`
	fragment MoneySeriesPointFields on MoneySeriesPoint {
		date
		value {
			...moneyFields
		}
	}
	${MONEY}
`;

export const REMOTE_ASSET = gql`
	fragment remoteAssetFields on RemoteAsset {
		id
		index
		size
		name
		url
	}
`;

const ADDRESS = gql`
	fragment addressFields on Address {
		id
		addressLine1
		addressLine2
		suburb
		city
		postCode
		administrativeArea
		country {
			id
			name
			isoAlpha2
		}
		status
	}
`;

export const BENEFICIAL_OWNER = gql`
	fragment beneficialOwnerFields on BeneficialOwner {
		id
		email
		firstName
		middleName
		lastName
		addressVerificationStatus
		basicDetailsVerificationStatus
		identityDocumentVerificationStatus
		biometricIdentityVerificationStatus
		address {
			...addressFields
			placeId
		}
		fatcaCrsDetails {
			isUsPerson
			usTaxIdentificationNumber
			fatcaCrsDeclaration
		}
		taxDetails {
			primaryTaxResidencyCountry {
				id
			}
			primaryTaxIdentificationNumber
			primaryTaxExemptReason
			secondaryTaxResidencyCountry {
				id
			}
			secondaryTaxIdentificationNumber
			secondaryTaxExemptReason
		}
	}
	${ADDRESS}
`;

export const ACCOUNT = gql`
	fragment accountFields on Account {
		id
		firstName
		middleName
		lastName
		preferredName
		email
		dayOfBirth
		monthOfBirth
		yearOfBirth
		timeZone
		isAccountLocked
		accountLockedReason
		hasProvidedAccountDetails
		phoneNumber {
			phoneNumber
			callingCode
		}
		country {
			id
			callingCode
		}
		address {
			...addressFields
		}
		placeOfBirth {
			id
			city
			country {
				id
				name
			}
		}
		primaryCitizenship {
			id
			name
		}
	}
	${ADDRESS}
`;

export const SESSION = gql`
	fragment sessionFields on Session {
		id
		token
		expires
		impersonatedBy {
			id
			firstName
			lastName
		}
	}
`;

export const FEATURE_FLAG = gql`
	fragment featureFlagFields on FeatureFlag {
		name
		isEnabled
	}
`;

export const ACCREDITATION = gql`
	fragment accreditationFields on Accreditation {
		id
		updated
		country {
			id
			name
		}
		... on WholesaleAccreditation {
			status
			document {
				viewURL
				template
				signatures {
					id
					isMe
					name
					email
					status
					signingURL
					relationship
				}
			}
		}
	}
`;

export const INVESTING_ENTITY = gql`
	fragment investingEntityFields on InvestingEntity {
		id
		address
		entityTypeEnum
		sourceOfFunds
		relationshipEnum
		nzPrescribedInvestorRate
		frequencyOfInvestment
		reasonForInvesting
		country {
			id
			name
		}
		accreditationCountry {
			id
			name
		}
		accreditation {
			...accreditationFields
		}
		documents {
			...remoteAssetFields
		}
	}
	${REMOTE_ASSET}
	${ACCREDITATION}
`;

export const ACCOUNT_NOTIFICATION = gql`
	fragment accountNotificationFields on AccountNotification {
		id
		status
		sortOrder
	}
`;

export const INVESTING_ENTITY_NOTIFICATION = gql`
	fragment investingEntityNotificationFields on InvestingEntityNotification {
		id
		status
		sortOrder
		... on ConfirmUnsupportedCountryAccreditationNotification {
			country {
				id
				name
			}
		}
	}
`;
